import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { X, AlertTriangle, WifiOff, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLesson, useStartLesson, useSubmitPhase, useCompleteLesson, useCheckAnswer } from '../hooks/cabin/useLesson';
import PhaseDispatcher from '../components/cabin/lesson/PhaseDispatcher';
import CompletePhase from '../components/cabin/lesson/CompletePhase';
import { useToast } from '../contexts/ToastContext';

const PLAYER_FLOW = {
  LOADING: 'loading',
  ERROR: 'error',
  PLAYING: 'playing',
  COMPLETE: 'complete',
};

const LOCAL_STORAGE_KEY_PREFIX = 'cabin_lesson_resume_';

function getResumeKey(lessonId) {
  return `${LOCAL_STORAGE_KEY_PREFIX}${lessonId}`;
}

function saveProgress(lessonId, data) {
  try {
    localStorage.setItem(getResumeKey(lessonId), JSON.stringify(data));
  } catch { /* noop */ }
}

function loadProgress(lessonId) {
  try {
    const raw = localStorage.getItem(getResumeKey(lessonId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearProgress(lessonId) {
  try {
    localStorage.removeItem(getResumeKey(lessonId));
  } catch { /* noop */ }
}

export default function CabinLessonPlayer() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useLanguage();
  // moduleSlug passed via navigation state from CabinModuleDetail or CabinVideoLessons
  const moduleSlug = location.state?.moduleSlug || null;
  const fromVideoLessons = location.state?.fromVideoLessons || false;
  const shouldReduce = useReducedMotion();
  const { showToast } = useToast();
  const startTime = useRef(Date.now());
  const initialized = useRef(false);

  // ── API hooks ────────────────────────────────────────────────────────────
  const { data: lesson, isLoading, isError } = useLesson(lessonId);
  const startLessonMutation = useStartLesson(lessonId);
  const submitPhaseMutation = useSubmitPhase(lessonId);
  const completeLessonMutation = useCompleteLesson(lessonId);
  const checkAnswerMutation = useCheckAnswer(lessonId);

  // ── Player state ──────────────────────────────────────────────────────────
  const [flow, setFlow] = useState(PLAYER_FLOW.LOADING);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [attemptNo, setAttemptNo] = useState(1);
  const [phaseAnswers, setPhaseAnswers] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [completionResult, setCompletionResult] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Derive lesson phases — useMemo prevents `|| []` from creating a new array
  // reference on every render, which would invalidate handlePhaseComplete's
  // useCallback dep chain and trigger unnecessary re-renders.
  const phases = useMemo(() => lesson?.phases || [], [lesson]);
  const totalPhases = phases.length;
  const currentPhase = phases[currentPhaseIndex];

  // ── Reset init guard when lessonId changes (navigation to a new lesson) ─
  useEffect(() => {
    initialized.current = false;
    setFlow(PLAYER_FLOW.LOADING);
    setCurrentPhaseIndex(0);
    setPhaseAnswers([]);
    setTotalScore(0);
    setCompletionResult(null);
    startTime.current = Date.now();
  }, [lessonId]);

  // ── Initialize ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!lesson) return;
    // Guard: only initialize once per lesson visit.
    // Without this, query invalidation after complete → lesson refetch →
    // useEffect rerun → setFlow(PLAYING) which overrides COMPLETE state.
    if (initialized.current) return;
    initialized.current = true;

    // Check for saved progress
    const saved = loadProgress(lessonId);

    setMaxScore(
      (lesson.phases || []).reduce((sum, p) => sum + (p.xp_in_phase || 0), 0)
    );

    // Start the lesson session
    startLessonMutation.mutate(undefined, {
      onSuccess: (data) => {
        const no = data?.attempt_no || 1;
        setAttemptNo(no);

        if (saved && saved.attemptNo === no) {
          // Resume
          setCurrentPhaseIndex(saved.currentPhaseIndex || 0);
          setPhaseAnswers(saved.phaseAnswers || []);
          setTotalScore(saved.totalScore || 0);
          setFlow(PLAYER_FLOW.PLAYING);
          showToast(t('lesson.resume_notice') || 'Kaldığınız yerden devam ediyorsunuz', 'info');
        } else {
          clearProgress(lessonId);
          setFlow(PLAYER_FLOW.PLAYING);
        }

        startTime.current = Date.now();
      },
      onError: () => {
        setFlow(PLAYER_FLOW.PLAYING);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson]);

  // Update flow to error if lesson load fails
  useEffect(() => {
    if (isError) setFlow(PLAYER_FLOW.ERROR);
  }, [isError]);

  // ── Complete handler (defined before phaseComplete for dependency ordering) ─
  const handleComplete = useCallback(
    (finalScore, _finalAnswers) => {
      const elapsed = Math.floor((Date.now() - startTime.current) / 1000);

      completeLessonMutation.mutate(
        {
          attemptNo,
          totalScore: finalScore,
          maxScore,
          totalTime: elapsed,
        },
        {
          onSuccess: (result) => {
            clearProgress(lessonId);
            setCompletionResult(result);
            setFlow(PLAYER_FLOW.COMPLETE);
          },
          onError: () => {
            // Even on error, show complete screen with estimated values
            clearProgress(lessonId);
            setCompletionResult({
              xp_earned: lesson?.xp_reward || 0,
              miles_earned: lesson?.miles_reward || 0,
              total_score: finalScore,
              max_score: maxScore,
              badges_unlocked: [],
            });
            setFlow(PLAYER_FLOW.COMPLETE);
          },
        }
      );
    },
    [attemptNo, maxScore, lessonId, completeLessonMutation, lesson]
  );

  // ── Phase complete handler ────────────────────────────────────────────────
  const handlePhaseComplete = useCallback(
    async ({ score, maxScore: phaseMaxScore, answers, timeSpent }) => {
      const phase = phases[currentPhaseIndex];
      if (!phase) return;

      const newTotalScore = totalScore + (score || 0);
      const newAnswers = [
        ...phaseAnswers,
        {
          phaseId: phase.phase_id,
          phaseType: phase.type,
          score,
          maxScore: phaseMaxScore,
          answers,
          timeSpent,
        },
      ];

      setTotalScore(newTotalScore);
      setPhaseAnswers(newAnswers);

      const nextIndex = currentPhaseIndex + 1;
      const isLast = nextIndex >= totalPhases;

      // Submit phase (idempotent). On the LAST phase we must AWAIT the submit
      // before calling complete — otherwise complete races ahead and reads a
      // stale total_score (0) → lesson wrongly marked failed (no checkmark).
      const submitPromise = submitPhaseMutation.mutateAsync({
        phaseId: phase.phase_id,
        score,
        maxScore: phaseMaxScore,
        answers,
        timeSpent,
        attemptNo,
      });
      if (isLast) {
        try { await submitPromise; } catch { /* idempotent; ignore */ }
      } else {
        submitPromise.catch(() => {});
      }

      // Save progress locally
      saveProgress(lessonId, {
        attemptNo,
        currentPhaseIndex: nextIndex,
        phaseAnswers: newAnswers,
        totalScore: newTotalScore,
        savedAt: new Date().toISOString(),
      });

      // Move to next phase or complete
      if (isLast) {
        handleComplete(newTotalScore, newAnswers);
      } else {
        setCurrentPhaseIndex(nextIndex);
      }
    },
    [phases, currentPhaseIndex, totalScore, phaseAnswers, attemptNo, lessonId, totalPhases, submitPhaseMutation, handleComplete]
  );

  // ── Exit handlers ────────────────────────────────────────────────────────
  // -- Live answer check (Bug 3) ----------------------------------------------
  // Exercise componentlerine verilir; tek soru dogrulugunu server-side dondurur.
  // checkAnswerMutation kararli ref degil; bu yuzden dep listesine eklenmedi
  // (TanStack mutation objesi her renderda yeni ref olabilir ama mutateAsync sabit).
  const checkAnswer = useCallback(
    async ({ phaseId, questionIndex, answer }) => {
      try {
        return await checkAnswerMutation.mutateAsync({ phaseId, questionIndex, answer });
      } catch {
        return null; // ag hatasi: component local fallback kullanir
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleExitRequest = useCallback(() => {
    setShowExitConfirm(true);
  }, []);

  const handleExitConfirm = useCallback(() => {
    clearProgress(lessonId);
    // Navigate explicitly to the owning page. Using navigate(-1) would
    // pop the history stack one entry at a time, which lands on the previously
    // visited lesson (lesson-to-lesson navigation pushes new entries) instead
    // of the originating page, causing a chained "exit" walk back through lessons.
    if (fromVideoLessons) {
      navigate('/programs/kabin-memurlugu/video-lessons');
    } else if (moduleSlug) {
      navigate(`/programs/kabin-memurlugu/modules/${moduleSlug}`);
    } else {
      navigate('/programs/kabin-memurlugu/modules');
    }
  }, [lessonId, moduleSlug, fromVideoLessons, navigate]);

  const handleExitCancel = useCallback(() => {
    setShowExitConfirm(false);
  }, []);

  // ── Lesson title for header ───────────────────────────────────────────────
  const lessonTitle =
    lang === 'tr'
      ? (lesson?.title?.tr || lesson?.title_tr || '')
      : (lesson?.title?.en || lesson?.title_en || lesson?.title?.tr || '');

  // ── Render helpers ────────────────────────────────────────────────────────
  if (flow === PLAYER_FLOW.LOADING || isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]"
        data-testid="lesson-player-loading"
        role="status"
        aria-label={t('lesson.loading') || 'Ders yükleniyor...'}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="text-[var(--brand-primary)] animate-spin" aria-hidden="true" />
          <p className="text-sm text-[var(--text-muted)]">
            {t('lesson.loading') || 'Ders yükleniyor...'}
          </p>
        </div>
      </div>
    );
  }

  if (flow === PLAYER_FLOW.ERROR) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] px-4"
        data-testid="lesson-player-error"
      >
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <WifiOff size={48} className="text-[var(--brand-danger)]" aria-hidden="true" />
          <p className="text-lg font-bold text-[var(--text-main)]">
            {t('lesson.error_title') || 'Ders yüklenemedi'}
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            {t('lesson.error_desc') || 'Bağlantını kontrol et ve tekrar dene.'}
          </p>
          <button
            onClick={() => {
              if (fromVideoLessons) {
                navigate('/programs/kabin-memurlugu/video-lessons');
              } else if (moduleSlug) {
                navigate(`/programs/kabin-memurlugu/modules/${moduleSlug}`);
              } else {
                navigate('/programs/kabin-memurlugu/modules');
              }
            }}
            className="px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl"
          >
            {t('common.go_back') || 'Geri Dön'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[var(--bg-app)] flex flex-col"
      data-testid="cabin-lesson-player"
    >
      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-white border-b border-[var(--border-light)] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Exit button */}
          <button
            onClick={handleExitRequest}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            aria-label={t('lesson.exit') || 'Dersten çık'}
            data-testid="lesson-exit-btn"
          >
            <X size={20} aria-hidden="true" />
          </button>

          {/* Progress bar */}
          {flow === PLAYER_FLOW.PLAYING && totalPhases > 0 && (
            <div className="flex-1 flex items-center gap-2" aria-hidden="true">
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[var(--brand-primary)] rounded-full"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${((currentPhaseIndex + 1) / totalPhases) * 100}%`,
                  }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <span className="text-xs font-semibold text-[var(--text-muted)] shrink-0">
                {currentPhaseIndex + 1}/{totalPhases}
              </span>
            </div>
          )}

          {/* Lesson title (truncated) */}
          {lessonTitle && (
            <p
              className="text-sm font-bold text-[var(--text-main)] truncate max-w-[180px] hidden sm:block"
              data-testid="lesson-player-title"
            >
              {lessonTitle}
            </p>
          )}
        </div>
      </div>

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        {/* Direct conditional render — AnimatePresence mode='wait' was causing transition hang */}
        {flow === PLAYER_FLOW.PLAYING && currentPhase && (
          <div key={`phase-${currentPhaseIndex}`}>
            {/* Phase title */}
            {(currentPhase?.title?.tr || currentPhase?.title?.en || currentPhase?.title_tr) && (
              <h2
                className="text-lg font-black text-[var(--text-main)] mb-6"
                data-testid="phase-title"
              >
                {lang === 'tr'
                  ? (currentPhase.title?.tr || currentPhase.title_tr)
                  : (currentPhase.title?.en || currentPhase.title_en || currentPhase.title?.tr)}
              </h2>
            )}

            <PhaseDispatcher
              phase={currentPhase}
              onPhaseComplete={handlePhaseComplete}
              checkAnswer={checkAnswer}
              data-testid="phase-dispatcher"
            />
          </div>
        )}

        {flow === PLAYER_FLOW.COMPLETE && (
          <div key="complete">
            <CompletePhase
              result={completionResult}
              lesson={lesson}
              moduleSlug={moduleSlug}
              data-testid="complete-phase"
            />
          </div>
        )}
      </div>

      {/* ── Exit confirmation dialog ─────────────────────────────────────── */}
      <AnimatePresence>
        {showExitConfirm && (
          <motion.div
            key="exit-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-dialog-title"
            data-testid="exit-confirm-dialog"
          >
            <motion.div
              initial={shouldReduce ? {} : { scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={shouldReduce ? {} : { scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 18, stiffness: 200 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-[0_20px_40px_rgba(0,0,0,0.15)]"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                  <AlertTriangle size={20} className="text-amber-600" aria-hidden="true" />
                </div>
                <h3 id="exit-dialog-title" className="font-black text-[var(--text-main)]">
                  {t('lesson.exitConfirm.title') || 'Dersten Çık?'}
                </h3>
              </div>

              <p className="text-sm text-[var(--text-muted)] mb-6">
                {t('lesson.exitConfirm.desc') || 'İlerlemeniz kaydedildi. Kaldığınız yerden devam edebilirsiniz.'}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleExitCancel}
                  className="flex-1 py-2.5 border-2 border-[var(--border-light)] text-[var(--text-main)] font-bold rounded-xl hover:border-[var(--brand-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                  data-testid="exit-cancel-btn"
                >
                  {t('lesson.exitConfirm.cancel') || 'Devam Et'}
                </button>
                <button
                  onClick={handleExitConfirm}
                  className="flex-1 py-2.5 bg-[var(--brand-danger)] text-white font-bold rounded-xl hover:opacity-90 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-danger)]"
                  data-testid="exit-confirm-btn"
                >
                  {t('lesson.exitConfirm.exit') || 'Çık'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
